import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('kinfolk_token'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verify token and get user on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/api/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Token verification failed:', error);
        // Token is invalid, clear it
        localStorage.removeItem('kinfolk_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, {
      email,
      password
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kinfolk_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, brokerType) => {
    const response = await axios.post(`${API}/api/auth/register`, {
      email,
      password,
      name,
      broker_type: brokerType
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kinfolk_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('kinfolk_token');
    setToken(null);
    setUser(null);
  };

  const updateBrokerType = async (brokerType) => {
    const response = await axios.put(`${API}/api/auth/broker-type`, {
      broker_type: brokerType
    });
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateBrokerType,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'master_admin',
    isMasterAdmin: user?.role === 'master_admin',
    isProcessor: user?.role === 'processor'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
